import moment from "moment";
import { getSalesByLevel3, getSalesSummaryCards } from "@/api/financialReport";
import { consoleHandlerError, extractErrorMessage } from "@/utils/message";
import { resolveDateRange, buildHeadersByPeriod, formatPeriodLabel } from "./utils/dateRange";

// En vistas year/ytd mostramos varios años hacia atrás, igual que ResultadosTab.
const YEARS_SHOWED = 4;

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

const state = () => ({
    /** Filtro de agrupación (Cuentas, Clientes, Tiendas, etc.) */
    activeSubFilter: "cuentas",
    /**
     * Cabeceras de columna calculadas tras el fetch.
     * Ejemplo: ["ene 2025", "feb 2025", ...]
     * @type {string[]}
     */
    monthHeaders: [],
    /**
     * Dict indexado por nombre de cuenta level_3.
     * Cada entrada es: { [periodoLabel]: number }
     * Ejemplo: { "Productos": { "ene 2025": 1500000, ... }, ... }
     * @type {Object.<string, Object.<string, number>>}
     */
    accountsData: {},
    /** Lista ordenada de cuentas level_3 presentes en los datos */
    accounts: [],
    /**
     * Acumulado del periodo + comparación contra ventana previa de igual largo.
     * Indexado por cuenta level_3 y con un agregado bajo la clave "__total__".
     * Ejemplo:
     *   {
     *     "Productos": { acc: 4_250_000, prevAcc: 3_780_000, pct: 0.1243 },
     *     __total__:   { acc: 5_430_000, prevAcc: 4_850_000, pct: 0.1196 },
     *   }
     * `pct` es null cuando prevAcc === 0 (frontend muestra "—").
     * @type {Object.<string, { acc: number, prevAcc: number, pct: number | null }>}
     */
    accumulatedData: {},
    /** Rango real devuelto por el backend */
    periodRange: { init_date: null, end_date: null },
    isLoading: false,
    error: null,
    /**
     * Resumen para las summary cards: { current_month, ytd, ltm }.
     * Cada entrada incluye period_start, period_end, amount y ratios mom/yoy.
     * Se calcula independiente de los filtros de la tabla porque necesita
     * comparables que pueden quedar fuera del rango visible (LTM, YoY, etc.).
     */
    summaryCards: null,
    isSummaryLoading: false,
    summaryError: null,
});

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

const EMPTY_ACC = { acc: 0, prevAcc: 0, pct: null };

const getters = {
    /**
     * Construye las filas de la tabla: una por cuenta level_3
     * más una fila de total al final. Cada fila incluye el acumulado del
     * periodo y su % vs ventana previa (renderizados en la 2da columna sticky).
     * @returns {Array<{ label: string, values: number[], accumulated: number, pct: number | null, isTotal: boolean }>}
     */
    tableRows: (state) => {
        return state.accounts.map((account) => {
            const acc = state.accumulatedData[account] ?? EMPTY_ACC;
            return {
                label: account,
                isTotal: false,
                values: state.monthHeaders.map(
                    (header) => state.accountsData[account]?.[header] ?? 0
                ),
                accumulated: acc.acc,
                pct: acc.pct,
            };
        });
    },

    /**
     * Fila de totales: suma de todas las cuentas por período. El acumulado y
     * el pct vienen del backend (clave __total__) para que sean consistentes
     * con la query agregada del rango previo.
     * @returns {{ label: string, values: number[], accumulated: number, pct: number | null, isTotal: boolean }}
     */
    totalRow: (state) => {
        const values = state.monthHeaders.map((header) => {
            return state.accounts.reduce((sum, account) => {
                return sum + (state.accountsData[account]?.[header] ?? 0);
            }, 0);
        });
        const totals = state.accumulatedData.__total__ ?? EMPTY_ACC;
        return {
            label: "Ventas Netas",
            values,
            isTotal: true,
            accumulated: totals.acc,
            pct: totals.pct,
        };
    },
};

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

const mutations = {
    SET_ACTIVE_SUB_FILTER(state, filter) {
        state.activeSubFilter = filter;
    },
    SET_SALES_DATA(state, { accounts, accountsData, monthHeaders, periodRange, accumulatedData }) {
        state.accounts = accounts;
        state.accountsData = accountsData;
        state.monthHeaders = monthHeaders;
        state.periodRange = periodRange;
        state.accumulatedData = accumulatedData;
    },
    SET_LOADING(state, isLoading) {
        state.isLoading = isLoading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
    SET_SUMMARY_CARDS(state, summary) {
        state.summaryCards = summary;
    },
    SET_SUMMARY_LOADING(state, isLoading) {
        state.isSummaryLoading = isLoading;
    },
    SET_SUMMARY_ERROR(state, error) {
        state.summaryError = error;
    },
};

// ---------------------------------------------------------------------------
// Actions
// ---------------------------------------------------------------------------

const actions = {
    async fetchSalesData({ state, commit, rootState }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const endMonth = rootState.reportsFilters.endMonth;
            const filterPeriod = rootState.financialReports.filterPeriod;

            let { initDate, endDate } = resolveDateRange(
                endMonth,
                rootState.reportsFilters.selectedPeriodRange
            );

            // En year/ytd extendemos el rango hacia atrás para mostrar varios
            // años como columnas, replicando el comportamiento de ResultadosTab.
            if (filterPeriod === "year" || filterPeriod === "ytd") {
                initDate = moment(endMonth)
                    .subtract(YEARS_SHOWED, "year")
                    .startOf("year")
                    .format("YYYY-MM-DD");
            }

            // Format groupBy parameter (e.g. 'cuentas', 'clientes', or category name)
            const groupBy = state.activeSubFilter.toLowerCase();

            const response = await getSalesByLevel3(initDate, endDate, groupBy, filterPeriod);
            const {
                accounts,
                report_data: reportData,
                period_range: periodRange,
                accumulated_data: accumulatedRaw,
            } = response.data;

            // Cabeceras según granularidad (mes/trimestre/año).
            const monthHeaders = buildHeadersByPeriod(
                periodRange.init_date,
                periodRange.end_date,
                filterPeriod
            );

            // El backend ya devuelve una fila por bucket truncado (DATE_TRUNC)
            // y aplica el cutoff YTD vía ytd_month_limit; acá solo formateamos.
            const accountsData = {};
            accounts.forEach((account) => {
                const row = {};
                (reportData[account] ?? []).forEach((item) => {
                    const label = formatPeriodLabel(item.period, filterPeriod);
                    row[label] = Number(item.total_amount);
                });
                // Freeze por fila: Vue 2 omite el observer recursivo en objetos congelados.
                accountsData[account] = Object.freeze(row);
            });

            // Normaliza la nomenclatura del backend (snake_case → camelCase) y
            // hace cast numérico explícito; el JSON puede serializar Decimal
            // como string en algunos paths del selector.
            const accumulatedData = {};
            Object.entries(accumulatedRaw ?? {}).forEach(([key, value]) => {
                accumulatedData[key] = Object.freeze({
                    acc: Number(value?.acc ?? 0),
                    prevAcc: Number(value?.prev_acc ?? 0),
                    pct: value?.pct == null ? null : Number(value.pct),
                });
            });

            commit("SET_SALES_DATA", {
                accounts: Object.freeze(accounts),
                accountsData: Object.freeze(accountsData),
                monthHeaders: Object.freeze(monthHeaders),
                periodRange: Object.freeze(periodRange),
                accumulatedData: Object.freeze(accumulatedData),
            });
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit("SET_ERROR", extractErrorMessage(error, "Error al cargar ventas."));
        } finally {
            commit("SET_LOADING", false);
        }
    },

    async fetchSalesSummary({ commit, rootState }) {
        commit("SET_SUMMARY_LOADING", true);
        commit("SET_SUMMARY_ERROR", null);

        try {
            const endDate = moment(rootState.reportsFilters.endMonth)
                .endOf("month")
                .format("YYYY-MM-DD");

            const response = await getSalesSummaryCards(endDate);
            commit("SET_SUMMARY_CARDS", Object.freeze(response.data));
        } catch (error) {
            consoleHandlerError({ error, prefix: "[salesReport]", silent: true });
            commit(
                "SET_SUMMARY_ERROR",
                extractErrorMessage(error, "Error al cargar resumen de ventas.")
            );
        } finally {
            commit("SET_SUMMARY_LOADING", false);
        }
    },
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};
